import { Controller } from "@hotwired/stimulus"
import { buildPreservedParams } from "../lib/preserved_params"

// Flatpickr is loaded as a global by the vendored script imported in
// app/javascript/application.js. We reference it via window because
// there is no npm entry — vendoring is intentional (see specs/2026-05-01-custom-timeframe-design.md §8).

export default class extends Controller {
  static targets = ["trigger", "startInput", "endInput", "popover", "applyButton", "resetLink"]
  static values = {
    basePath: String,
    preservedParams: { type: String, default: "{}" },
    initialStart: String,
    initialEnd: String,
    timezone: String,
    defaultPeriod: String,
    maxDate: String
  }

  connect() {
    this._buildPickers()
    this._onDocClick = (e) => {
      if (this.popoverTarget.classList.contains("hidden")) return
      if (this.element.contains(e.target)) return
      this._close()
    }
    this._onKey = (e) => {
      if (e.key === "Escape" && !this.popoverTarget.classList.contains("hidden")) {
        this._close()
      }
    }
    document.addEventListener("click", this._onDocClick, true)
    document.addEventListener("keydown", this._onKey)
  }

  disconnect() {
    document.removeEventListener("click", this._onDocClick, true)
    document.removeEventListener("keydown", this._onKey)
    if (this._startFp) this._startFp.destroy()
    if (this._endFp) this._endFp.destroy()
  }

  _buildPickers() {
    const fp = window.flatpickr
    if (!fp) {
      console.error("Flatpickr global not found; vendored script may have failed to load.")
      return
    }

    const baseOpts = {
      enableTime: false,
      dateFormat: "Y-m-d",
      altInput: true,
      altFormat: "M j, Y",
      maxDate: this.maxDateValue,
      static: true,
      onReady: (_d, _s, picker) => picker.calendarContainer.classList.add("orpheus-timeframe")
    }

    this._startFp = fp(this.startInputTarget, {
      ...baseOpts,
      defaultDate: this.initialStartValue || null,
      onChange: (sel) => {
        if (sel.length) this._endFp.set("minDate", sel[0])
        this._refreshApply()
      }
    })

    this._endFp = fp(this.endInputTarget, {
      ...baseOpts,
      defaultDate: this.initialEndValue || null,
      onChange: (sel) => {
        if (sel.length) this._startFp.set("maxDate", sel[0])
        this._refreshApply()
      }
    })

    // Flatpickr does not fire onChange for defaultDate set at initialization,
    // so reflect any seeded bounds and Apply state explicitly.
    if (this.initialStartValue) this._endFp.set("minDate", this._startFp.selectedDates[0])
    if (this.initialEndValue) this._startFp.set("maxDate", this._endFp.selectedDates[0])
    this._refreshApply()
  }

  _refreshApply() {
    const ready = this._startFp?.selectedDates?.length === 1 &&
                  this._endFp?.selectedDates?.length === 1
    this.applyButtonTarget.disabled = !ready
  }

  togglePopover(event) {
    event.stopPropagation()
    if (this.popoverTarget.classList.contains("hidden")) {
      this.popoverTarget.classList.remove("hidden")
    } else {
      this._close()
    }
  }

  _close() { this.popoverTarget.classList.add("hidden") }

  applyCustom() {
    const start = this._startFp?.selectedDates?.[0]
    const end = this._endFp?.selectedDates?.[0]
    if (!start || !end) return
    const params = this._preservedParams()
    params.set("period", "custom")
    params.set("start_date", `${this._formatDate(start)}T00:00`)
    params.set("end_date", `${this._formatDate(end)}T23:59`)
    this._navigate(params)
  }

  resetToDefault() {
    const params = this._preservedParams()
    params.set("period", this.defaultPeriodValue)
    params.delete("start_date")
    params.delete("end_date")
    this._navigate(params)
  }

  _navigate(params) {
    const url = `${this.basePathValue}?${params.toString()}`
    if (window.Turbo) {
      Turbo.visit(url)
    } else {
      window.location.href = url
    }
  }

  _preservedParams() {
    const obj = JSON.parse(this.preservedParamsValue || "{}")
    return buildPreservedParams(obj)
  }

  _formatDate(date) {
    const pad = (n) => String(n).padStart(2, "0")
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
  }
}
